import { useEffect, useState } from "react";
import { Plus } from "lucide-react";

import { useQuestionnaireStore } from "../store/useQuestionnaireStore";
import { useAuthStore } from "../store/useAuthStore";
import QuestionnaireList from "../components/questionnaires/QuestionnaireList";
import QuestionnaireForm from "../components/questionnaires/QuestionnaireForm";
import EditQuestionnaireModal from "../components/questionnaires/EditQuestionnaireModal";
import TemplatesLibrary from "../components/questionnaires/TemplatesLibrary";

const QuestionnairesPage = () => {
  const { questionnaires, getQuestionnaire } = useQuestionnaireStore();
  const { authUser } = useAuthStore();
  const isBD = authUser?.role === "Business Developer";

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [tab, setTab] = useState<"mine" | "templates">("mine");

  return (
    <div className={`min-h-screen bg-background px-4 lg:px-8 pb-10 ${isBD ? "pt-8" : "pt-20"}`}>
      <div className="max-w-[1400px] mx-auto flex flex-col gap-6">

        <section className="bg-surface-container-lowest rounded-xl border border-outline-variant card-shadow p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-[26px] font-bold text-on-surface tracking-tight">Manage Questionnaires</h1>
            <p className="text-[14px] text-on-surface-variant mt-1">
              Create and manage questionnaire forms for all services
            </p>
          </div>
          <button
            onClick={() => setDrawerOpen(true)}
            className="bg-primary text-white text-sm font-semibold px-5 py-2.5 rounded-lg flex items-center gap-2 hover:bg-[#004b9e] transition-colors shrink-0"
          >
            <Plus size={18} /> Create Questionnaire
          </button>
        </section>

        {/* Tabs: My Questionnaires | Pre-Built Templates */}
        <div className="flex gap-1 border-b border-outline-variant">
          {([["mine", "My Questionnaires"], ["templates", "Pre-Built Templates"]] as const).map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === k ? "border-primary text-primary" : "border-transparent text-on-surface-variant hover:text-on-surface"}`}
            >
              {label}
            </button>
          ))}
        </div>

        <section className="bg-surface-container-lowest rounded-xl border border-outline-variant card-shadow p-4 lg:p-6">
          {tab === "mine" ? <QuestionnaireList setEditing={setEditing} /> : <TemplatesLibrary />}
        </section>

        <div
          className={`fixed inset-y-0 right-0 w-full max-w-lg bg-white shadow-2xl transform transition-transform duration-500 ease-out z-50 flex flex-col ${
            drawerOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-100 bg-white shrink-0">
            <h2 className="text-2xl font-bold text-gray-800">
              Create Questionnaire
            </h2>

            <button
              onClick={() => setDrawerOpen(false)}
              className="p-3 rounded-full hover:bg-gray-100 transition-all duration-200 group"
            >
              <svg className="w-6 h-6 text-gray-600 group-hover:text-gray-800 transition" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-8 bg-white">
            <QuestionnaireForm onSuccess={() => setDrawerOpen(false)} />
          </div>
        </div>

        {drawerOpen && (
          <div
            onClick={() => setDrawerOpen(false)}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 transition-opacity duration-300"
          />
        )}

        <div className="fixed inset-0 z-60 flex items-center justify-center pointer-events-none">
          {editing && (
            <div className="pointer-events-auto">
              <EditQuestionnaireModal
                questionnaire={editing}
                onClose={() => setEditing(null)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionnairesPage;
